import { useEffect, useState } from "react";
import { FiChevronDown } from "react-icons/fi";
import OutsideClickHandler from "react-outside-click-handler";
import FormTypeService from "src/Services/API/formtype.service";
import { handleError } from "src/Common/function";

/**
 * Form types are tenant-editable data now, not a hard-coded list, so this
 * fetches them. Only active types are offered — a retired one stays resolvable
 * for forms that already recorded it (see getFormTypeLabel in formEnums), but
 * is not offered for new ones.
 *
 * Lists render the type name from the `formTypeRef` the API returns alongside
 * each row, so they do not need this component's data.
 */
/**
 * `includeAllOption` adds an "All" entry that selects "". Filters use it: with
 * no Apply/Clear buttons, choosing a value applies immediately, so the only way
 * back to unfiltered is from inside the dropdown. Forms that pick a real type
 * leave it off.
 */
const FormTypeDropdown = ({ selectedValue, onSelect, includeAllOption = false }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [formTypes, setFormTypes] = useState([]);

  useEffect(() => {
    FormTypeService.getAll()
      .then((response) => setFormTypes(response.data || []))
      .catch((error) => handleError(error));
  }, []);

  const selectedLabel =
    selectedValue !== null && selectedValue !== undefined
      ? formTypes.find((t) => t.id === selectedValue)?.name || (includeAllOption ? "All" : "Select")
      : includeAllOption
        ? "All"
        : "Select";

  return (
    <OutsideClickHandler onOutsideClick={() => setIsOpen(false)}>
      <div className="relative w-full">
        <div
          onClick={() => setIsOpen(!isOpen)}
          className="w-full flex items-center h-[30px] justify-between px-4 py-2 border rounded-[6px] text-gray-800 focus:outline-none focus:border-blue-500 cursor-pointer"
        >
          <span className="font-OpenSansRegular text-[12px]">{selectedLabel}</span>
          <FiChevronDown className="ml-2" size={15} />
        </div>
        {isOpen && (
          <ul className="absolute z-10 mt-2 w-full bg-white border rounded-md shadow-lg max-h-[300px] overflow-y-auto">
            {includeAllOption && (
              <li
                onClick={() => {
                  onSelect("");
                  setIsOpen(false);
                }}
                className="px-4 py-1 cursor-pointer hover:bg-gray-200 border-b-[1px] border-borderColor"
              >
                <span className="font-OpenSansRegular text-[12px]">All</span>
              </li>
            )}
            {formTypes.map((type) => (
              <li
                key={type.id}
                onClick={() => {
                  onSelect(type.id);
                  setIsOpen(false);
                }}
                className="px-4 py-1 cursor-pointer hover:bg-gray-200"
              >
                <span className="font-OpenSansRegular text-[12px]">{type.name}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </OutsideClickHandler>
  );
};

export default FormTypeDropdown;
